import React from 'react'
import DataRepo from '@api/datasource/data'
import QueryKeys from '@constants/queryKeys'

import { CreateEventTypeSchema, CreateEventTypeType, EventTypeType } from '@customTypes/event'

import { ModalProps } from '@customTypes/page'
import { useIsMobile } from '@hooks/mobile'
import { Button, LoadingOverlay, Modal, Text } from '@mantine/core'
import { useMutation, useQuery } from '@tanstack/react-query'
import { isLoadingMutation, isLoadingQuery } from '@utils/network'
import { useForm, zodResolver } from '@mantine/form'
import queryClient from '@api/datasource/query'
import { notifications } from '@mantine/notifications'
import Input from '@components/shared/input'
import ConfirmationModal from '@components/shared/confirmationModal'
import Cont from '@components/shared/cont'

const INITIAL: CreateEventTypeType = { name: '', color: '#FFFFFF' }

const EventTypeForm = (props: ModalProps<EventTypeType>) => {
  const { open, modal, data, size = 'lg', onClose: outerOnClose } = props

  const isMobile = useIsMobile()

  const [confirmExit, setConfirmExit] = React.useState(false)

  const formEventType = useForm<CreateEventTypeType>({
    initialValues: INITIAL,
    validate: zodResolver(CreateEventTypeSchema),
  })

  const eventTypesQuery = useQuery({
    queryKey: [QueryKeys.GET_EVENT_TYPES_KEY],
    queryFn: () => DataRepo.clientEntityService.getEventTypeOptions(),
    enabled: Boolean(open),
  })

  const createEventTypeMutation = useMutation<CreateEventTypeType, Error, CreateEventTypeType>({
    mutationFn: (values) => DataRepo.clientEntityService.createEventType(values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_EVENT_TYPES_KEY],
        refetchType: 'all',
      })

      formEventType.resetDirty()
      formEventType.reset()
      outerOnClose?.()

      notifications.show({
        message: 'Tipo de estado creado correctamente',
        title: 'Éxito',
        color: 'green',
      })
    },
    onError: (error) => {
      notifications.show({
        message: error.message || 'Error al crear el tipo de estado',
        title: 'Error',
        color: 'red',
      })
    },
  })

  const updateEventTypeMutation = useMutation<void, Error, EventTypeType>({
    mutationFn: (values) => DataRepo.clientEntityService.updateEventType(values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_EVENT_TYPES_KEY],
        refetchType: 'all',
      })

      formEventType.resetDirty()
      formEventType.reset()
      outerOnClose?.()

      notifications.show({
        message: 'Tipo de estado actualizado correctamente',
        title: 'Éxito',
        color: 'green',
      })
    },
    onError: (error) => {
      notifications.show({
        message: error.message || 'Error al actualizar el tipo de estado',
        title: 'Error',
        color: 'red',
      })
    },
  })

  React.useEffect(() => {
    if (open && data) {
      formEventType.setValues(data)
    } else if (!open) {
      formEventType.setValues(INITIAL)
    }
    formEventType.resetDirty()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data])

  const isLoading = isLoadingMutation(createEventTypeMutation, updateEventTypeMutation)

  const isLoadingEventType = isLoadingQuery(eventTypesQuery)

  const content = (
    <form
      onSubmit={formEventType.onSubmit((values) => {
        const existingEventType = eventTypesQuery.data?.some(
          (status) => status.name === values.name,
        )
        const isSameEventType = data && data.name === values.name

        if (existingEventType && !isSameEventType) {
          notifications.show({
            message: 'Ya existe un estado con ese nombre',
            title: 'Error',
            color: 'red',
          })
          return
        }

        if (data) {
          updateEventTypeMutation.mutate(values as EventTypeType)
        } else {
          createEventTypeMutation.mutate(values)
        }
      })}
    >
      <div className="cd-flex cd-flex-col cd-gap-y-[1rem]">
        <LoadingOverlay
          loaderProps={{ type: 'dots' }}
          overlayProps={{ radius: 'sm', blur: 2 }}
          visible={isLoadingEventType}
          zIndex={1000}
        />
        <Input
          capitalize
          label="Nombre"
          placeholder="Ingrese el nombre"
          typeInput="text"
          {...formEventType.getInputProps('name')}
          onBlur={() => formEventType.validateField('name')}
        />

        <Input
          label="Color"
          typeInput="color"
          withEyeDropper={false}
          {...formEventType.getInputProps('color')}
          onBlur={() => formEventType.validateField('color')}
        />

        <Button
          className="cd-mt-[1rem]"
          disabled={!formEventType.isValid()}
          loaderProps={{ type: 'dots' }}
          loading={isLoading}
          type="submit"
        >
          {data ? 'Actualizar Tipo de Evento' : 'Crear Tipo de Evento'}
        </Button>
      </div>
    </form>
  )

  if (modal) {
    return (
      <Modal
        centered
        fullScreen={isMobile}
        opened={Boolean(open)}
        size={size}
        title={data ? 'Actualizar Tipo de Evento' : 'Crear Tipo de Evento'}
        withCloseButton={modal}
        onClose={() => {
          if (formEventType.isDirty()) {
            setConfirmExit(true)
          } else {
            onClose()
          }
        }}
      >
        {content}
        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Descartar"
          open={confirmExit}
          title="Descartar cambios"
          onCancel={() => setConfirmExit(false)}
          onConfirm={onClose}
        >
          <Text className="cd-text-base">
            Has realizado cambios en el formulario, ¿Deseas cerrarlo y descartar los cambios?
          </Text>
        </ConfirmationModal>
      </Modal>
    )
  }

  return <Cont>{content}</Cont>

  function onClose() {
    outerOnClose?.()
    setConfirmExit(false)
    formEventType.reset()
  }
}

export default EventTypeForm
